import {
  AuthAPI,
  AuthCreateFormTokenResult,
  AuthDirectLoginResult,
  AuthDirectLoginWithVerificationCodeQuery,
  AuthDirectRequestVerificationCodeQuery,
  GlobalAuthState,
  MpxResponseError,
  SupplementedAxiosError,
} from '@cibo/core'
import { logNonRequestError, logRequestError } from '@cibo/ui'
import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { useDispatch } from 'react-redux'
import {
  authDirectLoginSuccess,
  authDirectLoginWithVerificationCode,
  authSessionValid,
  refreshUserInfo,
} from '../store'
import { QUERY_KEY } from './queryKey'
import { AUTH_FORM_TOKEN_TIMEOUT } from './useFormToken'

export const useRequestPasswordResetCode = (
  query: Partial<Pick<AuthDirectRequestVerificationCodeQuery, 'username'>>,
  options?: Partial<UseQueryOptions>
) =>
  useQuery<AuthCreateFormTokenResult, MpxResponseError, AuthCreateFormTokenResult, any>({
    queryKey: [QUERY_KEY.VERIFICATION_CODE],
    // @ts-ignore
    queryFn: async () => {
      const formAuthToken = await AuthAPI.createFormToken()
      return AuthAPI.directLoginRequestVerificationCode({
        formAuthToken: formAuthToken.data.formAuthToken,
        formAuthCode: formAuthToken.data.captchaData.formAuthCode,
        ...(query as Pick<AuthDirectRequestVerificationCodeQuery, 'username'>),
      })
        .then(() => formAuthToken.data)
        .catch(e => logRequestError(e))
    },
    gcTime: AUTH_FORM_TOKEN_TIMEOUT,
    enabled: !!query.username,
    ...options,
  })

/**
 * @todo
 * refactor without redux
 */
export const useLoginWithVerificationCode = () => {
  const dispatch = useDispatch()

  return useMutation({
    mutationFn: (query: AuthDirectLoginWithVerificationCodeQuery) =>
      AuthAPI.directLoginWithVerificationCode(query),
    onSuccess: async ({ data }: AxiosResponse<AuthDirectLoginResult>) => {
      GlobalAuthState.setAuthStateValid(data.userJwt)
      const fullUser = await AuthAPI.userInfo()

      /* @todo refactor without dispatch
      
      also, GET THIS: the response from logging in returns a user record that appears to
      be missing some post-processing step on the sever. I have noticed with several users
      that their list of roles and permissions (kind of important) differ between the login
      response and the AuthAPI.userInfo() response.  The `userInfo` version appears to be
      the fully computed version of the user document, but is missing the userJwt which is
      necessary to pass to redux for the persistent storage.

      */
      dispatch(
        authDirectLoginSuccess({ ...fullUser, data: { ...fullUser.data, userJwt: data.userJwt } })
      )
      dispatch(authSessionValid())
      dispatch(refreshUserInfo())
      dispatch(authDirectLoginWithVerificationCode)
    },
    onError: (error: AxiosError | unknown) => {
      if ((error as AxiosError).isAxiosError) {
        logRequestError(error as SupplementedAxiosError, {
          response: (error as AxiosError).response?.data,
          location: 'useLoginWithVerificationCode',
        })
      } else {
        logNonRequestError(error, {
          location: 'useLoginWithVerificationCode non request error',
        })
      }
    },
  })
}
